"use client";

import { useMemo, useState } from "react";
import { z } from "zod";
import type { ResourceCalculator as ResourceCalculatorConfig } from "@/lib/content/entries/lead-magnets";
import { useWebMcpTool } from "@/lib/webmcp/react";

type ResourceCalculatorProps = {
  calculator: ResourceCalculatorConfig;
};

type CalculatorResult = {
  primary: string;
  secondary: string;
  tertiary: string;
  quaternary: string;
};

function initialValues(calculator: ResourceCalculatorConfig): Record<string, number> {
  return Object.fromEntries(calculator.fields.map((field) => [field.id, field.defaultValue]));
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
    style: "currency",
    currency: "USD",
  }).format(value);
}

function calculateAiVideo(values: Record<string, number>, copy: Record<string, string>): CalculatorResult {
  const runtimeMinutes = values.runtimeMinutes ?? 1;
  const outputFormats = values.outputFormats ?? 1;
  const languageVersions = values.languageVersions ?? 1;
  const reviewRounds = values.reviewRounds ?? 1;
  const complexity = values.complexity ?? 1;
  const productionUnits =
    runtimeMinutes * 1.6 + outputFormats * 0.75 + languageVersions * 1.25 + reviewRounds * 0.8;
  const complexityMultiplier = 1 + (complexity - 1) * 0.35;
  const minDays = Math.max(2, Math.ceil(productionUnits * complexityMultiplier));
  const maxDays = minDays + Math.ceil(complexity * 3 + reviewRounds);
  const scopeSignal =
    maxDays <= 8 ? copy.smallScope : maxDays <= 18 ? copy.standardScope : copy.complexScope;
  const reviewRisk =
    reviewRounds <= 2 && languageVersions <= 2
      ? copy.lowRisk
      : reviewRounds <= 4 && languageVersions <= 4
        ? copy.reviewRisk
        : copy.heavyRisk;
  const workingDaysUnit = copy.workingDaysUnit;

  return {
    primary: `${minDays}-${maxDays} ${workingDaysUnit}`,
    secondary: scopeSignal,
    tertiary: reviewRisk,
    quaternary: copy.nextStep,
  };
}

function calculateWhiteLabelMargin(values: Record<string, number>, copy: Record<string, string>): CalculatorResult {
  const resalePrice = values.resalePrice ?? 0;
  const fulfillmentCost = values.fulfillmentCost ?? 0;
  const internalHours = values.internalHours ?? 0;
  const hourlyCost = values.hourlyCost ?? 0;
  const revisionBuffer = values.revisionBuffer ?? 0;
  const internalCost = internalHours * hourlyCost;
  const bufferedFulfillmentCost = fulfillmentCost * (1 + revisionBuffer / 100) + internalCost;
  const grossMargin = resalePrice - bufferedFulfillmentCost;
  const marginRate = resalePrice > 0 ? (grossMargin / resalePrice) * 100 : 0;
  const marginSignal =
    marginRate >= 45 ? copy.strongMargin : marginRate >= 30 ? copy.watchMargin : copy.thinMargin;

  return {
    primary: formatUsd(grossMargin),
    secondary: `${Math.round(marginRate)}%`,
    tertiary: formatUsd(bufferedFulfillmentCost),
    quaternary: `${marginSignal} ${copy.nextStep}`,
  };
}

export default function ResourceCalculator({ calculator }: ResourceCalculatorProps) {
  const [values, setValues] = useState<Record<string, number>>(() => initialValues(calculator));
  const toolSchema = useMemo(
    () =>
      z
        .object(
          Object.fromEntries(
            calculator.fields.map((field) => [
              field.id,
              z.number().min(field.min).max(field.max).multipleOf(field.step),
            ]),
          ) as z.ZodRawShape,
        )
        .strict(),
    [calculator.fields],
  );
  useWebMcpTool({
    action: "calculate_resource_estimate",
    instance: calculator.kind.replaceAll("-", "_"),
    schema: toolSchema,
    execute: (input) => {
      const nextValues = input as Record<string, number>;
      const nextResult =
        calculator.kind === "ai-video-cost-time"
          ? calculateAiVideo(nextValues, calculator.resultCopy)
          : calculateWhiteLabelMargin(nextValues, calculator.resultCopy);

      setValues(nextValues);
      return { status: "success", uiState: { values: nextValues, result: nextResult } };
    },
  });
  const result = useMemo(() => {
    if (calculator.kind === "ai-video-cost-time") {
      return calculateAiVideo(values, calculator.resultCopy);
    }

    return calculateWhiteLabelMargin(values, calculator.resultCopy);
  }, [calculator.kind, calculator.resultCopy, values]);
  const resultRows = [
    { label: calculator.resultLabels.primary, value: result.primary },
    { label: calculator.resultLabels.secondary, value: result.secondary },
    { label: calculator.resultLabels.tertiary, value: result.tertiary },
    { label: calculator.resultLabels.quaternary, value: result.quaternary },
  ];

  return (
    <div className="redesign-split" data-resource-calculator={calculator.kind}>
      <div className="redesign-list-card">
        <h3>{calculator.title}</h3>
        <p>{calculator.intro}</p>
        <div className="redesign-feature-list">
          {calculator.fields.map((field) => (
            <label className="redesign-feature" key={field.id}>
              <span className="redesign-feature__index">{field.suffix ?? field.step}</span>
              <span>
                <span className="redesign-card__eyebrow">{field.label}</span>
                <input
                  type="range"
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={values[field.id] ?? field.defaultValue}
                  onChange={(event) =>
                    setValues((currentValues) => ({
                      ...currentValues,
                      [field.id]: Number(event.target.value),
                    }))
                  }
                />
                <strong>
                  {values[field.id] ?? field.defaultValue}
                  {field.suffix ? ` ${field.suffix}` : ""}
                </strong>
                <small>{field.help}</small>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className="redesign-list-card">
        <h3>{calculator.resultLabels.primary}</h3>
        <dl className="redesign-hero__stats" aria-label={calculator.title}>
          {resultRows.map((row) => (
            <div className="redesign-stat" key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
        <p>{calculator.disclaimer}</p>
      </div>
    </div>
  );
}
